import { useState, useRef } from 'react';
import { View, Text, TextInput as RNTextInput, Pressable } from 'react-native';
import { CurrencyInput } from './CurrencyInput';
import { Button } from './Button';
import { LifeEventCategoryField } from './LifeEventCategoryField';
import { LifeEventTaxFields } from './LifeEventTaxFields';
import { YearReferencePicker } from './YearReferencePicker';
import { RecurringPurchaseFields } from './RecurringPurchaseFields';
import { useConfirmationDialog } from './ConfirmationDialog';
import { useWebClickOutside } from '../hooks/useWebClickOutside';
import {
  DEFAULT_PURCHASE_GROWTH_RATE_PERCENT,
  EVENT_DESCRIPTION_PLACEHOLDERS,
  LIFE_EVENT_TYPE_LABELS,
  formatLifeEventTaxLabel,
  formatYearReferenceLabel,
  formatRecurrence,
  isLifeEventInflowType,
  normalizeLifeEventTaxTreatment,
  resolveEventYearReference,
  getDefaultRecurringEndYear,
  parseAmount,
  parsePercent,
  parseRepeatInterval,
  createYearReferenceState,
  eventToYearReferenceState,
  parseYearReferenceInput,
  type GoalInflowTaxTreatment,
  type GoalYearReference,
  type EventYearReferencePerson,
  type GoalEvent,
  type LifeEventFormValues,
  type YearReferencePickerValue,
} from '../lib/life-events';
import { formatCurrency } from '../lib/utils';
import { webPrivacyProps } from '../lib/privacy';

export interface LifeEventListProps {
  events: GoalEvent[];
  profiles: EventYearReferencePerson[];
  currentFireYear: number | null | undefined;
  defaultPlanEndYear: number;
  onUpdate: (id: string, values: LifeEventFormValues) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  isDemo?: boolean;
  isLoading?: boolean;
}

export function LifeEventList({
  events,
  profiles,
  currentFireYear,
  defaultPlanEndYear,
  onUpdate,
  onDelete,
  isDemo = false,
  isLoading = false,
}: LifeEventListProps) {
  const currentYear = new Date().getFullYear();
  const listRef = useRef<any>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editStartYearInput, setEditStartYearInput] = useState<YearReferencePickerValue>(() =>
    createYearReferenceState(String(currentYear + 5)),
  );
  const [editIsRecurring, setEditIsRecurring] = useState(false);
  const [editRepeatEveryYears, setEditRepeatEveryYears] = useState('');
  const [editEndYearInput, setEditEndYearInput] = useState<YearReferencePickerValue>(() =>
    createYearReferenceState(String(defaultPlanEndYear)),
  );
  const [editGrowthRatePercent, setEditGrowthRatePercent] = useState(
    DEFAULT_PURCHASE_GROWTH_RATE_PERCENT,
  );
  const [editIsTaxable, setEditIsTaxable] = useState(false);
  const [editTaxTreatment, setEditTaxTreatment] =
    useState<GoalInflowTaxTreatment>('ordinary_income');
  const [editBudgetCategoryId, setEditBudgetCategoryId] = useState<
    number | null
  >(null);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const { confirmDestructive } = useConfirmationDialog();

  const beginEdit = (event: GoalEvent) => {
    setEditError(null);
    setEditingId(event.id);
    setEditDescription(event.description);
    setEditAmount(event.amount != null ? String(event.amount) : '');
    setEditStartYearInput(eventToYearReferenceState(event, 'start', String(event.year)));
    setEditIsRecurring(event.type === 'expense' && event.repeatEveryYears != null);
    setEditRepeatEveryYears(
      event.repeatEveryYears != null ? String(event.repeatEveryYears) : '',
    );
    setEditEndYearInput(
      event.repeatEveryYears != null
        ? eventToYearReferenceState(
            event,
            'end',
            getDefaultRecurringEndYear(String(event.year), defaultPlanEndYear),
          )
        : createYearReferenceState(String(defaultPlanEndYear)),
    );
    setEditGrowthRatePercent(
      event.growthRate != null
        ? String(Number(event.growthRate) * 100)
        : DEFAULT_PURCHASE_GROWTH_RATE_PERCENT,
    );
    setEditIsTaxable(Boolean(event.isTaxable));
    setEditTaxTreatment(
      normalizeLifeEventTaxTreatment(event.isTaxable, event.taxTreatment) ??
        'ordinary_income',
    );
    setEditBudgetCategoryId(event.budgetCategoryId ?? null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setYearPickerOpen(false);
    setCategoryPickerOpen(false);
    setEditDescription('');
    setEditAmount('');
    setEditStartYearInput(createYearReferenceState(String(currentYear + 5)));
    setEditIsRecurring(false);
    setEditRepeatEveryYears('');
    setEditEndYearInput(createYearReferenceState(String(defaultPlanEndYear)));
    setEditGrowthRatePercent(DEFAULT_PURCHASE_GROWTH_RATE_PERCENT);
    setEditIsTaxable(false);
    setEditTaxTreatment('ordinary_income');
    setEditBudgetCategoryId(null);
    setEditError(null);
  };

  const handleSave = async (event: GoalEvent) => {
    setEditError(null);
    const cleanDescription = editDescription.trim();
    const parsedAmount = parseAmount(editAmount);

    if (!cleanDescription) {
      setEditError('Description is required.');
      return;
    }
    if (parsedAmount === null) {
      setEditError('Amount must be a valid number.');
      return;
    }

    const parsedStart = parseYearReferenceInput({
      label: 'Start',
      value: editStartYearInput,
      fallbackYear: event.year,
      profiles,
      currentFireYear,
    });
    if (!parsedStart.parsed) {
      setEditError(parsedStart.error);
      return;
    }

    let repeatInterval: number | undefined;
    let parsedEnd: {
      year: number;
      ref: GoalYearReference;
      personId?: string;
      age?: number;
      resolvedYear: number | null;
    } | null = null;
    let growthRate: number | undefined;

    if (event.type === 'expense' && editIsRecurring) {
      const ri = parseRepeatInterval(editRepeatEveryYears);
      const gr = parsePercent(editGrowthRatePercent);

      if (ri == null) {
        setEditError('Repeat interval must be at least 1 year.');
        return;
      }
      if (gr == null) {
        setEditError('Growth rate must be a valid percent.');
        return;
      }

      const nextParsedEnd = parseYearReferenceInput({
        label: 'End',
        value: editEndYearInput,
        fallbackYear: event.endYear ?? defaultPlanEndYear,
        profiles,
        currentFireYear,
      });
      if (!nextParsedEnd.parsed) {
        setEditError(nextParsedEnd.error);
        return;
      }
      if (
        nextParsedEnd.parsed.resolvedYear != null &&
        parsedStart.parsed.resolvedYear != null &&
        nextParsedEnd.parsed.resolvedYear < parsedStart.parsed.resolvedYear
      ) {
        setEditError('End year must be the same as or after the first year.');
        return;
      }

      repeatInterval = ri;
      parsedEnd = nextParsedEnd.parsed;
      growthRate = gr;
    }

    const values: LifeEventFormValues = {
      type: event.type,
      description: cleanDescription,
      amount: parsedAmount,
      year: parsedStart.parsed.year,
      startYearRef: parsedStart.parsed.ref,
      ...(parsedStart.parsed.personId ? { startYearPersonId: parsedStart.parsed.personId } : {}),
      ...(parsedStart.parsed.age != null ? { startYearAge: parsedStart.parsed.age } : {}),
      ...(isLifeEventInflowType(event.type)
        ? {
            isTaxable: editIsTaxable,
            taxTreatment: editIsTaxable ? editTaxTreatment : null,
          }
        : {}),
      ...(event.type === 'expense'
        ? { budgetCategoryId: editBudgetCategoryId }
        : {}),
      ...(event.type === 'expense' && repeatInterval != null
        ? {
            repeatEveryYears: repeatInterval,
            endYear: parsedEnd?.year,
            ...(parsedEnd != null ? { endYearRef: parsedEnd.ref } : {}),
            ...(parsedEnd?.personId ? { endYearPersonId: parsedEnd.personId } : {}),
            ...(parsedEnd?.age != null ? { endYearAge: parsedEnd.age } : {}),
            growthRate,
          }
        : {}),
    };

    setSavingId(event.id);
    try {
      await onUpdate(event.id, values);
      cancelEdit();
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : 'Could not update event. Please try again.',
      );
    } finally {
      setSavingId(null);
    }
  };

  const executeDelete = async (event: GoalEvent) => {
    setDeletingId(event.id);
    try {
      await onDelete(event.id);
      if (editingId === event.id) cancelEdit();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Could not delete event. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDelete = async (event: GoalEvent) => {
    const confirmed = await confirmDestructive({
      title: 'Delete event?',
      message: `This will remove "${event.description}" from your plan.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    await executeDelete(event);
  };

  useWebClickOutside(
    [listRef],
    editingId !== null && !yearPickerOpen && !categoryPickerOpen,
    cancelEdit,
  );

  if (events.length === 0) {
    if (isLoading) return null;
    return (
      <Text className="text-sm text-gray-400 text-center py-3">
        No events added yet. Add major expenses, windfalls, or liquidity events to see their impact.
      </Text>
    );
  }

  return (
    <View ref={listRef}>
      {events.map((event) => (
        <View
          key={event.id}
          className="py-2.5 border-b border-gray-50 dark:border-gray-700 last:border-0"
        >
          {editingId === event.id ? (
            <>
              {editError && (
                <View className="rounded-xl bg-red-50 dark:bg-red-950 px-3 py-2 mb-2">
                  <Text className="text-sm text-red-600 dark:text-red-400">{editError}</Text>
                </View>
              )}

              <Text className="text-xs text-gray-400 mb-2">
                {LIFE_EVENT_TYPE_LABELS[event.type]}
              </Text>

              <RNTextInput
                value={editDescription}
                onChangeText={(text) => {
                  setEditDescription(text);
                  setEditError(null);
                }}
                placeholder={EVENT_DESCRIPTION_PLACEHOLDERS[event.type]}
                className="border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-800 dark:text-gray-200 mb-2"
                {...webPrivacyProps('sensitive')}
              />

              <View className="mb-2">
                <CurrencyInput
                  value={editAmount}
                  onChangeText={(text) => {
                    setEditAmount(text);
                    setEditError(null);
                  }}
                  placeholder="Amount (optional)"
                  size="sm"
                />
              </View>

              {event.type === 'expense' && (
                <LifeEventCategoryField
                  value={editBudgetCategoryId}
                  onChange={(categoryId) => {
                    setEditBudgetCategoryId(categoryId);
                    setEditError(null);
                  }}
                  onPickerOpenChange={setCategoryPickerOpen}
                />
              )}

              {isLifeEventInflowType(event.type) && (
                <LifeEventTaxFields
                  isTaxable={editIsTaxable}
                  taxTreatment={editTaxTreatment}
                  onTaxableChange={(value) => {
                    setEditIsTaxable(value);
                    setEditError(null);
                  }}
                  onTaxTreatmentChange={(value) => {
                    setEditTaxTreatment(value);
                    setEditError(null);
                  }}
                />
              )}

              <View className="mb-2">
                <YearReferencePicker
                  label={event.type === 'expense' && editIsRecurring ? 'Start year' : 'Year'}
                  value={editStartYearInput}
                  onChange={(next) => {
                    setEditStartYearInput(next);
                    setEditError(null);
                  }}
                  profiles={profiles}
                  currentFireYear={currentFireYear}
                  onOpenChange={setYearPickerOpen}
                  calendarPlaceholder={
                    event.type === 'expense' && editIsRecurring ? 'First year' : 'Year'
                  }
                />
              </View>

              {event.type === 'expense' && (
                <RecurringPurchaseFields
                  isRecurring={editIsRecurring}
                  onSelectOneTime={() => {
                    setEditIsRecurring(false);
                    setEditRepeatEveryYears('');
                    setEditEndYearInput(createYearReferenceState(String(defaultPlanEndYear)));
                    setEditError(null);
                  }}
                  onSelectRepeats={() => {
                    setEditIsRecurring(true);
                    setEditError(null);
                    if (!editRepeatEveryYears) setEditRepeatEveryYears('1');
                    if (!editEndYearInput.year) {
                      setEditEndYearInput(
                        createYearReferenceState(
                          getDefaultRecurringEndYear(editStartYearInput.year, defaultPlanEndYear),
                        ),
                      );
                    }
                  }}
                  repeatEveryYears={editRepeatEveryYears}
                  onRepeatEveryYearsChange={(text) => {
                    setEditRepeatEveryYears(text);
                    setEditError(null);
                  }}
                  endYear={editEndYearInput.year}
                  onEndYearChange={(text) => {
                    setEditEndYearInput((current) => ({ ...current, year: text }));
                    setEditError(null);
                  }}
                  growthRatePercent={editGrowthRatePercent}
                  onGrowthRatePercentChange={(text) => {
                    setEditGrowthRatePercent(text);
                    setEditError(null);
                  }}
                  showEndYearInput={false}
                />
              )}

              {event.type === 'expense' && editIsRecurring && (
                <View className="mb-3">
                  <YearReferencePicker
                    label="End year"
                    value={editEndYearInput}
                    onChange={(next) => {
                      setEditEndYearInput(next);
                      setEditError(null);
                    }}
                    profiles={profiles}
                    currentFireYear={currentFireYear}
                    onOpenChange={setYearPickerOpen}
                    calendarPlaceholder="End year"
                    agePlaceholder="End age"
                  />
                </View>
              )}

              <View className="flex-row gap-2">
                <Button
                  size="sm"
                  className="flex-1"
                  onPress={() => handleSave(event)}
                  loading={savingId === event.id}
                >
                  Save
                </Button>
                <Button size="sm" variant="ghost" onPress={cancelEdit}>
                  Cancel
                </Button>
              </View>

              {!isDemo && (
                <View className="flex-row justify-end mt-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onPress={() => handleDelete(event)}
                    loading={deletingId === event.id}
                    disabled={savingId === event.id}
                  >
                    Delete
                  </Button>
                </View>
              )}
            </>
          ) : (
            <Pressable
              onPress={isDemo || editingId !== null ? undefined : () => beginEdit(event)}
              disabled={isDemo || editingId !== null}
            >
              <View className="flex-row items-center justify-between gap-2">
                <View className="flex-1">
                  <Text
                    className="text-sm font-medium text-gray-800 dark:text-gray-200"
                    {...webPrivacyProps('private')}
                  >
                    {event.description}
                  </Text>
                  <Text className="text-xs text-gray-400">
                    {LIFE_EVENT_TYPE_LABELS[event.type]} ·{' '}
                    {formatYearReferenceLabel(
                      {
                        ref: event.startYearRef,
                        year: event.year,
                        personId: event.startYearPersonId,
                        age: event.startYearAge,
                      },
                      profiles,
                      currentFireYear,
                    )}
                  </Text>
                  {formatLifeEventTaxLabel(event) ? (
                    <Text className="text-xs text-gray-400 mt-0.5">
                      {formatLifeEventTaxLabel(event)}
                    </Text>
                  ) : null}
                  {formatRecurrence(
                    event.repeatEveryYears,
                    event.growthRate,
                    resolveEventYearReference(
                      {
                        ref: event.endYearRef,
                        year: event.endYear,
                        personId: event.endYearPersonId,
                        age: event.endYearAge,
                      },
                      profiles,
                      currentFireYear,
                    ) ?? event.endYear,
                  ) && (
                    <Text className="text-xs text-gray-400 mt-0.5">
                      {formatRecurrence(
                        event.repeatEveryYears,
                        event.growthRate,
                        resolveEventYearReference(
                          {
                            ref: event.endYearRef,
                            year: event.endYear,
                            personId: event.endYearPersonId,
                            age: event.endYearAge,
                          },
                          profiles,
                          currentFireYear,
                        ) ?? event.endYear,
                      )}
                    </Text>
                  )}
                  {event.repeatEveryYears != null && (
                    <Text className="text-xs text-gray-400 mt-0.5">
                      Ends{' '}
                      {formatYearReferenceLabel(
                        {
                          ref: event.endYearRef,
                          year: event.endYear,
                          personId: event.endYearPersonId,
                          age: event.endYearAge,
                        },
                        profiles,
                        currentFireYear,
                      )}
                    </Text>
                  )}
                </View>

                <View className="items-end gap-1">
                  {event.amount != null && (
                    <Text
                      className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                      {...webPrivacyProps('financial')}
                    >
                      {formatCurrency(Number(event.amount), { compact: true })}
                    </Text>
                  )}
                </View>
              </View>
            </Pressable>
          )}
        </View>
      ))}
    </View>
  );
}
